import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import styled, { keyframes } from "styled-components";
import { LOGIN_REQUEST, CLEAR_ERRORS } from "constants/actions";
import { DomainContext } from "layouts/LayoutContext";
import GoogleAuth from "./GoogleAuth";
import UpgradePassword from "./UpgradePassword";
import MandatoryLinks from "./MandatoryLinks";
import UserErrorMessages from "./UserErrorMessages";
import { ValidateEmail } from "utilities/Validate";
import dashboard from "assets/images/dashboard.png";

// Animations
const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const fadeInUp = keyframes`
  0% { opacity: 0; transform: translateY(30px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const logoScale = keyframes`
  0% { transform: scale(0.8); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
`;

/* ----------------------------------
   MAIN LAYOUT CONTAINERS
---------------------------------- */
const PageContainer = styled.div`
  display: flex;
  height: 100vh;
  width: 100%;
  position: relative;
  font-family: "Raleway", sans-serif;
  overflow: hidden;
  justify-content: center;

  @media (max-width: 992px) {
    flex-direction: column;
    height: auto;
    min-height: 100vh;
  }
`;

// LEFT SIDE: Login panel
const LoginPanel = styled.div`
  width: 50%;
  background-color: #fff;
  display: flex;
  flex-direction: column;

  @media (max-width: 992px) {
    width: 100%;
    order: 1;
  }
`;

const FormContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  /* Doubled from 420px to 840px */
  width:350px;
  margin: 0 auto;
  padding:2em 0;
  animation: ${fadeInUp} 0.6s ease forwards;

`;

const Logo = styled.img`
  width: 100px;
  margin: 0 auto 1.5rem;
  display: block;
  animation: ${logoScale} 0.5s ease forwards;
`;

const LoginTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #2A3039;
  text-align: center;
  margin-bottom: 2rem;
  font-family: "Raleway", sans-serif;
`;

// RIGHT SIDE: Animated background content
const ContentPanel = styled.div`
  width: 50%;
  background: linear-gradient(270deg, #2D70E2, #4A8AF4, #2D70E2);
  background-size: 400% 400%;
  animation: ${gradientAnimation} 30s ease infinite;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  @media (max-width: 992px) {
    width: 100%;
    display:none;
    min-height: 320px;
    order: 2;
  }
`;

const ContentInner = styled.div`
  padding: 100px 40px 0;
  color: white;
  position: relative;
  z-index: 2;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    padding: 40px 30px 0;
  }
`;

const Headline = styled.h1`
  font-size: 42px;
  font-weight: 800;
  max-width: 350px;
  margin-bottom: 16px;
  line-height: 1.2;
  animation: ${fadeInUp} 0.6s ease forwards;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Subheadline = styled.p`
  font-size: 16px;
  line-height: 1.5;
  margin-bottom: 24px;
  opacity: 0.9;
  max-width: 340px;
  animation: ${fadeInUp} 0.6s ease forwards;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const DashboardImage = styled.img`
  width: 70%;
  margin-top: auto;
  margin-left: 30%;
  z-index: 1;
  filter: drop-shadow(0px 10px 20px rgba(0, 0, 0, 0.15));
  opacity:0;
  animation: ${fadeInUp} 0.6s ease forwards;

  @media (max-width: 992px) {
    max-width: 400px;
    width: 80%;
    margin-left: 10%;
  }
`;

/* ----------------------------------
   FORM & INPUT STYLES
---------------------------------- */
const InputWrapper = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
`;

const InputField = styled.input`
  width: 100%;
  padding: 0.75rem 0.5rem;
  border: none;
  border-bottom: 2px solid ${({ error }) => (error ? "#e74c3c" : "#ccc")};
  font-size: 1rem;
  font-family: "Raleway", sans-serif;
  outline: none;
  transition: border-color 0.3s ease;

  &:focus {
    border-color: ${({ error }) => (error ? "#e74c3c" : "#2D70E2")};
  }

  &:focus + label,
  &:not(:placeholder-shown) + label {
    top: -0.75rem;
    left: 0;
    font-size: 0.8rem;
    color: ${({ error }) => (error ? "#e74c3c" : "#2D70E2")};
  }
`;

const InputLabel = styled.label`
  position: absolute;
  top: 0.75rem;
  left: 0.5rem;
  font-size: 1rem;
  color: #999;
  transition: all 0.3s ease;
  pointer-events: none;
`;

const ErrorMessage = styled.p`
  color: #e74c3c;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  margin-bottom: 0;
`;

const PasswordInputWrapper = styled.div`
  position: relative;
`;

const ToggleIcon = styled.span`
  position: absolute;
  right: 0.5rem;
  top: 0.8rem;
  cursor: pointer;
  color: #bbb;

  &:hover {
    color: #2D70E2;
  }
`;

/* ----------------------------------
   EXTRAS: REMEMBER, FORGOT, ETC.
---------------------------------- */
const BottomRowContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
`;

const CheckboxLabel = styled.label`
  font-size: 0.9rem;
  color: #2A3039;
  margin-left: 0.35rem;
  cursor: pointer;
  font-family: "Raleway", sans-serif;
`;

const StyledCheckbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: #2D70E2;
  cursor: pointer;
`;

const ForgotPasswordLink = styled(Link)`
  font-size: 0.9rem;
  color: #2D70E2;
  font-family: "Raleway", sans-serif;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

/* ----------------------------------
   BUTTONS & MESSAGES
---------------------------------- */
const SubmitButton = styled.button`
  width: 100%;
  padding: 0.9rem 1.25rem;
  margin-top: 1.4rem;
  border: none;
  border-radius: 8px;
  background-color: #2D70E2;
  color: #fff;
  font-size: 1rem;
  font-family: "Raleway", sans-serif;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(45, 112, 226, 0.25);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SuccessContainer = styled.div`
  background-color: #e6f7e6;
  color: #2ecc71;
  border: 1px solid #2ecc71;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  text-align: center;
  font-family: "Raleway", sans-serif;
`;

const OrSeparator = styled.div`
  text-align: center;
  margin: 1.5rem 0;
  font-size: 0.9rem;
  color: #999;
  position: relative;

  &:before,
  &:after {
    content: "";
    display: inline-block;
    width: 40%;
    height: 1px;
    background: #ccc;
    vertical-align: middle;
    margin: 0 0.5rem;
  }
`;

const GoogleRow = styled.div`
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  animation: ${fadeInUp} 0.6s ease forwards;
`;

const FooterLinks = styled.div`
  margin-top: 1.5rem;
  text-align: center;
  font-size: 0.75rem;
  color: #9CA3AF;
  font-family: "Raleway", sans-serif;
`;

/* ----------------------------------
   LOGIN FORM COMPONENT
---------------------------------- */
const LoginForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [pristine, setPristine] = useState(true);
  const [loading, setLoading] = useState(false);
  const [userAuth, setUserAuth] = useState(false);
  const [passwordReset, setPasswordReset] = useState(null);

  const Domain = useContext(DomainContext);

  const { get_auth } = useSelector(
    (state) => ({
      get_auth: state.auth,
    }),
    shallowEqual
  );

  // Populate userAuth / next steps / password reset
  useEffect(() => {
    setUserAuth(get_auth);
  }, [get_auth]);

  useEffect(() => {
    if (get_auth?.reset_password) {
      setPasswordReset(get_auth.reset_password);
    }
  }, [get_auth?.reset_password]);

  // Form validation
  useEffect(() => {
    const errors = {};

    if (!email) {
      errors.email = { error: true, message: "Please enter an email" };
    } else if (!ValidateEmail(email)) {
      errors.email = { error: true, message: "Please enter a valid email" };
    }

    if (!password) {
      errors.password = { error: true, message: "Please enter a password" };
    }

    setFormErrors(errors);
  }, [email, password, pristine]);

  const { from } = location.state || {};

  // If user is authenticated, redirect
  useEffect(() => {
    if (userAuth?.token) {
      if (from) {
        navigate(from.pathname);
      } else {
        navigate("/settings/select-organization");
      }
    }
  }, [userAuth, navigate, from]);

  // If user must upgrade password
  if (userAuth?.next_steps === 2) {
    return <UpgradePassword />;
  }

  const onSubmit = (e) => {
    e.preventDefault(); // prevent default form submission behavior
    setLoading(true);

    // If any field is invalid, show errors
    if (Object.values(formErrors).some((err) => err.error)) {
      setPristine(false);
      setLoading(false);
      return;
    }

    // Clear previous errors, dispatch login
    dispatch({ type: CLEAR_ERRORS });
    dispatch({
      type: LOGIN_REQUEST,
      payload: {
        email,
        password,
        remember: rememberMe,
      },
    });

    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      onSubmit();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleRememberMe = () => {
    setRememberMe(!rememberMe);
  };

  return (
    <PageContainer>
      {/* LEFT: Login Panel */}
      <LoginPanel>
        <FormContainer>
          {/* Domain Logo */}
          <Logo src={Domain.fullImage} alt="Company Logo" />

          {/* Title (or Domain-specific text) */}
          <LoginTitle>{Domain.loginText || "Login to your account"}</LoginTitle>

          {/* Display any global or backend error messages */}
          <UserErrorMessages />

          {/* If password reset message from the backend */}
          {passwordReset?.message && (
            <SuccessContainer>{passwordReset.message}</SuccessContainer>
          )}

          {/* Wrap your inputs in a form */}
          <form onSubmit={onSubmit}>
            {/* Email input */}
            <InputWrapper>
              <InputField
                type="email"
                id="email"
                placeholder=" "
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={formErrors.email?.error && !pristine}
                aria-describedby={formErrors.email?.error ? "email-error" : undefined}
              />
              <InputLabel htmlFor="email">Email</InputLabel>
              {formErrors.email?.error && !pristine && (
                <ErrorMessage id="email-error">{formErrors.email.message}</ErrorMessage>
              )}
            </InputWrapper>

            {/* Password input */}
            <InputWrapper>
              <PasswordInputWrapper>
                <InputField
                  type={showPassword ? "text" : "password"}
                  id="password"
                  placeholder=" "
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={formErrors.password?.error && !pristine}
                  aria-describedby={formErrors.password?.error ? "password-error" : undefined}
                />
                <InputLabel htmlFor="password">Password</InputLabel>
                <ToggleIcon onClick={togglePasswordVisibility}>
                  {showPassword ? "👁️‍🗨️" : "👁️"}
                </ToggleIcon>
              </PasswordInputWrapper>
              {formErrors.password?.error && !pristine && (
                <ErrorMessage id="password-error">
                  {formErrors.password.message}
                </ErrorMessage>
              )}
            </InputWrapper>

            {/* Bottom row: remember me + forgot password */}
            <BottomRowContainer>
              <CheckboxContainer>
                <StyledCheckbox
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={toggleRememberMe}
                />
                <CheckboxLabel htmlFor="rememberMe">Remember me</CheckboxLabel>
              </CheckboxContainer>
              <ForgotPasswordLink to="/account/forgot-password">
                Forgot password?
              </ForgotPasswordLink>
            </BottomRowContainer>

            {/* Submit button */}
            <SubmitButton type="submit" disabled={loading}>
              {loading ? "Signing In..." : "Sign In"}
            </SubmitButton>
          </form>

          {/* Google SSO if enabled */}
          {Domain.showGoogleSso && (
            <>
              <OrSeparator>OR</OrSeparator>
              <GoogleRow>
                <GoogleAuth login />
              </GoogleRow>
            </>
          )}

          {/* Footer / mandatory links */}
          <FooterLinks>
            <MandatoryLinks Domain={Domain} />
          </FooterLinks>
        </FormContainer>
      </LoginPanel>

      {/* RIGHT: Gradient Content Panel */}
      {Domain.name !== "CAAT" && (
        <ContentPanel>
          <ContentInner>
            <Headline>AI-powered culture growth</Headline>
            <Subheadline>
              Action your people data 10x faster and enable your leaders with
              curated, evidence-based change management plans.
            </Subheadline>
          </ContentInner>
          <DashboardImage src={dashboard} alt="Platform Dashboard" />
        </ContentPanel>
      )}
    </PageContainer>
  );
};

export default LoginForm;
